/**
 * Load + keep active product alerts in sync via telemetry WebSocket updates.
 */
import * as React from "react";
import { useWebSocket } from "../../hooks/useWebSocket";
import {
  fetchProductActiveAlerts,
  type ProductAlertEpisode,
} from "../../lib/api/products";
import {
  activeAlertsFromMeasures,
  type ActiveAlertSummary,
} from "./ActiveAlertsCountBadge";

function toSummary(alerts: Array<{
  code: string;
  name?: string | null;
  type?: string | null;
  severity?: number | null;
  openedAt?: string | null;
}>): ActiveAlertSummary[] {
  return alerts.map((a) => ({
    code: a.code,
    name: a.name ?? a.code,
    type: a.type ?? null,
    severity: a.severity ?? null,
    openedAt: a.openedAt ?? null,
  }));
}

function mergeEarliestOpenedAt(
  prev: ProductAlertEpisode[],
  next: ProductAlertEpisode[],
): ProductAlertEpisode[] {
  const prevBy = new Map(prev.map((a) => [a.code.toUpperCase(), a]));
  return next.map((a) => {
    const old = prevBy.get(a.code.toUpperCase());
    if (!old?.openedAt || !a.openedAt) return a;
    const oldT = new Date(old.openedAt).getTime();
    const newT = new Date(a.openedAt).getTime();
    if (Number.isFinite(oldT) && Number.isFinite(newT) && oldT < newT) {
      return { ...a, openedAt: old.openedAt };
    }
    return a;
  });
}

export function useProductActiveAlerts(
  productId: string | null | undefined,
  token: string | null,
): {
  alerts: ActiveAlertSummary[];
  episodes: ProductAlertEpisode[];
  loading: boolean;
} {
  const [episodes, setEpisodes] = React.useState<ProductAlertEpisode[]>([]);
  const [loading, setLoading] = React.useState(false);
  const { subscribe, unsubscribe } = useWebSocket();

  const reload = React.useCallback(async () => {
    if (!token || !productId) return;
    try {
      const res = await fetchProductActiveAlerts(token, productId);
      setEpisodes((prev) => mergeEarliestOpenedAt(prev, res.data ?? []));
    } catch {
      /* ignore — badge is best-effort */
    }
  }, [token, productId]);

  React.useEffect(() => {
    if (!token || !productId) {
      setEpisodes([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void reload().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, productId, reload]);

  React.useEffect(() => {
    if (!productId) return;
    const topic = `telemetry:products:${productId}`;
    const handler = (msg: { payload?: unknown }) => {
      const payload = msg.payload as { measures?: Record<string, { codes?: Array<{ code?: string; label?: string }> }> } | null;
      if (payload?.measures) {
        const fromWs = activeAlertsFromMeasures(payload.measures);
        setEpisodes((prev) => {
          // Prefer WS for membership; keep earlier openedAt when code persists.
          const prevBy = new Map(prev.map((a) => [a.code.toUpperCase(), a]));
          return fromWs.map((a) => {
            const old = prevBy.get(a.code.toUpperCase());
            return {
              // Preserve the real incident id from a prior API reload if we have
              // one yet — the WS payload itself doesn't carry it.
              incidentId: old?.incidentId ?? a.code,
              code: a.code,
              name: a.name ?? a.code,
              type: a.type ?? old?.type ?? null,
              severity: a.severity ?? old?.severity ?? null,
              description: old?.description ?? null,
              openedAt: old?.openedAt ?? new Date().toISOString(),
              closedAt: null,
              active: true,
              resolvedSource: null,
              resolvedByUserId: null,
              resolvedByUserName: null,
              resolutionNote: null,
            };
          });
        });
      }
      // Reconcile openedAt / names from API shortly after
      void reload();
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [productId, subscribe, unsubscribe, reload]);

  return {
    alerts: toSummary(episodes),
    episodes,
    loading,
  };
}

/**
 * Map of productId → active alert summaries for list pages.
 * Seeds from per-product fetches for visible ids; updates from telemetry:products:*.
 */
export function useProductsActiveAlertsMap(
  productIds: string[],
  token: string | null,
): Map<string, ActiveAlertSummary[]> {
  const [map, setMap] = React.useState<Map<string, ActiveAlertSummary[]>>(() => new Map());
  const { subscribe, unsubscribe } = useWebSocket();
  const idsKey = productIds.slice().sort().join(",");

  React.useEffect(() => {
    if (!token || productIds.length === 0) return;
    let cancelled = false;
    const ids = [...productIds];

    void (async () => {
      const entries = await Promise.all(
        ids.map(async (id): Promise<[string, ActiveAlertSummary[]]> => {
          try {
            const res = await fetchProductActiveAlerts(token, id);
            return [id, toSummary(res.data ?? [])];
          } catch {
            return [id, []];
          }
        }),
      );
      if (cancelled) return;
      setMap((prev) => {
        const next = new Map(prev);
        for (const [id, alerts] of entries) next.set(id, alerts);
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- idsKey captures productIds
  }, [token, idsKey]);

  React.useEffect(() => {
    const handler = (msg: { type?: string; channel?: string; payload?: unknown }) => {
      if (msg.type !== "telemetry.update" || !msg.channel) return;
      const productId = msg.channel.split(":")[2];
      if (!productId) return;
      const payload = msg.payload as {
        measures?: Record<string, { codes?: Array<{ code?: string; label?: string }> }>;
      } | null;
      if (!payload?.measures) return;
      const alerts = activeAlertsFromMeasures(payload.measures);
      setMap((prev) => {
        const next = new Map(prev);
        next.set(productId, alerts);
        return next;
      });
    };
    subscribe(["telemetry:products:*"], handler);
    return () => unsubscribe(["telemetry:products:*"], handler);
  }, [subscribe, unsubscribe]);

  return map;
}
